import { FC } from 'react';
import { twJoin } from 'tailwind-merge';

import Heading from 'molecules/Heading';
import Section from 'molecules/Section';
import { splitSectionProps } from 'molecules/Section/config';

import { ComparisonCard, ComparisonCardsConfigProps } from 'components/ComparisonCards/config';

interface ReasonBlockProps {
  label?: string;
  text?: string;
  lineClass: string;
}

const ReasonBlock: FC<ReasonBlockProps> = ({ label, text, lineClass }) =>
  text ? (
    <div className={twJoin('flex flex-col gap-3 border-t-2 pt-4', lineClass)}>
      <p className="font-roboto text-lg font-bold leading-7 text-white">{label}</p>
      <p className="font-roboto text-sm leading-5 text-gray-300 lg:text-md lg:leading-[1.3]">{text}</p>
    </div>
  ) : null;

interface ComparisonCardViewProps {
  card: ComparisonCard;
  gradientClass: string;
  // Divider color above WHY/HOW, per card as in Figma (teal card #65D5DA, blue card #A6C6EB).
  lineClass: string;
  whyLabel?: string;
  howLabel?: string;
}

// No scroll-fade animation on these cards: while the card fades in, the glow behind it shows
// through and the module looks broken for a beat. The cards render in place.
const ComparisonCardView: FC<ComparisonCardViewProps> = ({ card, gradientClass, lineClass, whyLabel, howLabel }) => (
  <div
    className={twJoin(
      'flex h-full flex-col gap-8 rounded-lg px-6 py-10 backdrop-blur-[5px] sm:gap-10 sm:px-8 sm:py-12 lg:gap-14 lg:px-[50px] lg:py-[52px]',
      gradientClass,
    )}
  >
    <div className="flex flex-col gap-2.5">
      {card.title && (
        <p className="font-spaceGrotesk text-3xl font-semibold leading-[38px] text-white lg:text-4xl lg:leading-[44px] lg:tracking-[-0.72px]">
          {card.title}
        </p>
      )}
      {card.description && (
        <p className="font-roboto text-md leading-6 text-gray-300 lg:text-xl lg:leading-[30px]">{card.description}</p>
      )}
    </div>
    <div className="grid grid-cols-1 gap-x-8 gap-y-6 md:grid-cols-2 lg:gap-x-12">
      <ReasonBlock label={whyLabel} text={card.why} lineClass={lineClass} />
      <ReasonBlock label={howLabel} text={card.how} lineClass={lineClass} />
    </div>
  </div>
);

const ComparisonCards: FC<ComparisonCardsConfigProps> = props => {
  const { sectionProps, rest } = splitSectionProps(props);
  const { eyebrow, heading, whyLabel, howLabel, leftCard, rightCard, showBackgroundGlow } = rest;
  // Builder entries saved while the field was absent have no showBackgroundGlow value; treat
  // missing as ON so the glow only disappears when explicitly toggled off in Builder.
  const showGlow = showBackgroundGlow !== false;

  return (
    <Section {...sectionProps} sidePadding="none">
      {/* The parent <section> is overflow-hidden at full viewport width, so it clips the
          full-bleed glow layer; no extra clipping wrapper here (a nested clip would cut the
          glow at the 1280px container edge and read as a hard vertical seam). */}
      <div className="w-full">
        <div className="mx-auto w-full max-w-screen-xl px-5 py-14 sm:px-8 sm:py-16 lg:px-12 lg:py-24">
          {(eyebrow || heading) && (
            <div className="mb-10 sm:mb-14">
              <Heading as="h2" alignment="center" eyebrow={eyebrow} heading={heading} />
            </div>
          )}

          <div className="relative">
            {showGlow && (
              // Figma backdrop ellipses (798x220 teal at 33% and 773x220 ocean-blue at 70% of the
              // 1440 frame, blur 104, vertically centered on the cards). The layer spans the full
              // viewport width so the glow reaches the ends of the side spacing instead of being
              // contained by the section container; vertically it stays within the cards' height.
              <div
                aria-hidden
                className="pointer-events-none absolute inset-y-0 left-1/2 z-0 w-screen -translate-x-1/2"
              >
                {/* Elliptical radial gradients instead of filter blur. Every gradient lives in a
                    FULL-SIZE box and is placed via its `at` position, so each ellipse fades to zero
                    inside the box and the teal and blue washes blend into one another with no box
                    edges or seams. Desktop (lg+): one horizontal ellipse centered on each card
                    (~33% / ~70% of the viewport, as in Figma). Below lg (mobile + tablet, stacked
                    cards): the Figma ellipses are rotated 90deg, one vertical oval behind each card
                    (teal top, blue bottom) haloing above, below, the sides and between them. */}
                <div className="absolute inset-0 hidden bg-[radial-gradient(ellipse_38%_46%_at_33%_50%,rgba(101,213,218,0.55),rgba(101,213,218,0))] lg:block" />
                <div className="absolute inset-0 hidden bg-[radial-gradient(ellipse_38%_46%_at_70%_50%,rgba(69,115,174,0.6),rgba(69,115,174,0))] lg:block" />
                {/* The mobile boxes extend past the card band so the halo fades out softly above the
                    first card and below the last one instead of cutting at the card edge. */}
                <div className="absolute inset-x-0 inset-y-[-8%] bg-[radial-gradient(ellipse_90%_33%_at_50%_35%,rgba(101,213,218,0.55),rgba(101,213,218,0))] lg:hidden" />
                <div className="absolute inset-x-0 inset-y-[-8%] bg-[radial-gradient(ellipse_90%_33%_at_50%_67%,rgba(69,115,174,0.6),rgba(69,115,174,0))] lg:hidden" />
              </div>
            )}

            <div className="relative z-10 grid grid-cols-1 items-stretch gap-8 lg:grid-cols-2 lg:gap-4">
              {leftCard && (
                <ComparisonCardView
                  card={leftCard}
                  gradientClass="bg-dual-feature-left"
                  lineClass="border-primary-300"
                  whyLabel={whyLabel}
                  howLabel={howLabel}
                />
              )}
              {rightCard && (
                <ComparisonCardView
                  card={rightCard}
                  gradientClass="bg-dual-feature-right"
                  lineClass="border-primary-1300"
                  whyLabel={whyLabel}
                  howLabel={howLabel}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};

export default ComparisonCards;
